import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { environment } from 'environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

type I18nVersion = { version?: string; files?: Record<string, string> };

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
    private _httpClient = inject(HttpClient);
    private readonly _apiBaseUrl = this._normalizeBase(
        environment.supertokens?.apiDomain || environment.baseUrl
    );

    private _categoryTranslationsCache = new Map<
        string,
        Observable<Record<string, string>>
    >();

    private _version$: Observable<I18nVersion> = this._httpClient
        .get<I18nVersion>(`./i18n/version.json`)
        .pipe(
            catchError(() => of({} as I18nVersion)),
            shareReplay(1)
        );

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get translation
     *
     * @param lang
     */
    getTranslation(lang: string): Observable<Translation> {
        const base = `./i18n/${lang}.json`;
        return this._version$.pipe(
            map((v) => v.files?.[lang] || v.version || ''),
            switchMap((hash) => {
                const translation$ = this._httpClient.get<Translation>(
                    hash ? `${base}?v=${hash}` : base
                );
                return forkJoin({
                    translation: translation$,
                    categories: this._getCategoryTranslations(lang),
                }).pipe(
                    map(({ translation, categories }) => {
                        const fallback = (translation as any)?.categories ?? {};
                        const resolved =
                            categories && Object.keys(categories).length > 0
                                ? categories
                                : fallback;
                        return {
                            ...translation,
                            categories: resolved,
                        };
                    })
                );
            })
        );
    }

    private _getCategoryTranslations(
        lang: string
    ): Observable<Record<string, string>> {
        const normalized = (lang || '').toLowerCase();
        if (this._categoryTranslationsCache.has(normalized)) {
            return this._categoryTranslationsCache.get(normalized)!;
        }

        if (!this._apiBaseUrl) {
            const fallback$ = of({}).pipe(shareReplay(1));
            this._categoryTranslationsCache.set(normalized, fallback$);
            return fallback$;
        }

        const url = `${this._apiBaseUrl}/api/category/translations?lang=${encodeURIComponent(normalized)}`;
        const request$ = this._httpClient
            .get<{ categories?: Record<string, string> }>(url)
            .pipe(
                map((response) => response?.categories ?? {}),
                catchError(() => of({})),
                shareReplay(1)
            );

        this._categoryTranslationsCache.set(normalized, request$);
        return request$;
    }

    private _normalizeBase(value?: string): string {
        if (!value) {
            return '';
        }

        return value.replace(/\/+$/, '');
    }
}
